import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Event as RouterEvent, NavigationEnd, Router } from '@angular/router';

import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { filter } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ApplicationInsightsService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private appInsights: ApplicationInsights | null = null;
  private initialized = false;

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    if (this.initialized || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const config = environment.applicationInsights;

    if (!config.connectionString) {
      console.warn('Application Insights connection string not configured');
      return;
    }

    try {
      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: config.instrumentationKey,
          connectionString: config.connectionString,
          enableAutoRouteTracking: true,
          disableExceptionTracking: false,
          enablePerfMgr: true,
          samplingPercentage: 25,
          enableDebug: false,
          disableFetchTracking: false,
          disableAjaxTracking: false,
          disableCookiesUsage: false,
          disableTelemetry: false,
          maxBatchInterval: 15000,
          maxBatchSizeInBytes: 10000,
        },
      });

      this.appInsights.loadAppInsights();
      this.initialized = true;

      this.setupRouteTracking();
    } catch (error) {
      console.error('Failed to initialize Application Insights:', error);
    }
  }

  private setupRouteTracking(): void {
    this.router.events
      .pipe(filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.trackPageView(event.urlAfterRedirects);
      });
  }

  trackPageView(pageName?: string): void {
    if (!this.initialized || !this.appInsights) return;

    try {
      this.appInsights.trackPageView({
        name: pageName || document.title,
        uri: window.location.href,
      });
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  trackEvent(
    name: string,
    properties?: Record<string, string>,
    measurements?: Record<string, number>,
  ): void {
    if (!this.initialized || !this.appInsights) return;

    try {
      this.appInsights.trackEvent({ name, properties, measurements });
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  trackException(
    error: Error,
    severityLevel?: number,
    properties?: Record<string, string>,
    measurements?: Record<string, number>,
  ): void {
    if (!this.initialized || !this.appInsights) return;

    try {
      this.appInsights.trackException({
        exception: error,
        severityLevel,
        properties,
        measurements,
      });
    } catch (trackError) {
      console.error('Failed to track exception:', trackError);
    }
  }

  trackMetric(
    name: string,
    average: number,
    properties?: Record<string, string>,
    count?: number,
  ): void {
    if (!this.initialized || !this.appInsights) return;

    try {
      this.appInsights.trackMetric({ name, average, properties, sampleCount: count });
    } catch (error) {
      console.error('Failed to track metric:', error);
    }
  }

  setUserContext(userId: string): void {
    if (!this.initialized || !this.appInsights) return;

    try {
      this.appInsights.setAuthenticatedUserContext(userId, undefined);
    } catch (error) {
      console.error('Failed to set user context:', error);
    }
  }

  clearUserContext(): void {
    if (!this.initialized || !this.appInsights) return;

    try {
      this.appInsights.clearAuthenticatedUserContext();
    } catch (error) {
      console.error('Failed to clear user context:', error);
    }
  }

  flush(): void {
    if (!this.initialized || !this.appInsights) return;

    try {
      this.appInsights.flush();
    } catch (error) {
      console.error('Failed to flush Application Insights:', error);
    }
  }

  get isEnabled(): boolean {
    return this.initialized && !!this.appInsights;
  }
}
